import { ArrowRight, CircleAlert, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CardStatus, KycStatus, type KycSnapshot } from '@shared/types';

import { Panel, SectionHeading } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { KycStatusBadge } from '@/components/ui/Badge';
import { InlineError } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { Stepper, type Step } from '@/components/verification/Stepper';
import { DocumentStep } from '@/components/verification/DocumentStep';

import { useKyc } from '@/lib/queries';
import { useSubmitKyc } from '@/lib/mutations';
import { toMessage } from '@/lib/api';
import { absoluteDateTime } from '@/lib/format';

const PASSPORT_REQUIREMENTS = [
  'Главный разворот с фотографией, лежащий на ровной поверхности — все четыре угла в кадре.',
  'Без бликов на ламинате и без пальцев, закрывающих часть страницы.',
  'Каждая строка машиночитаемой зоны внизу должна читаться.',
];

const SELFIE_REQUIREMENTS = [
  'Держите раскрытый паспорт рядом с лицом; в кадре резкими должны быть оба.',
  'Ровный фронтальный свет. Без головного убора, тёмных очков и фильтров.',
  'Страница с фотографией должна читаться на самом селфи, а не только в загруженном паспорте.',
];

export default function VerificationPage() {
  const toast = useToast();
  const kyc = useKyc();
  const submitKyc = useSubmitKyc();

  if (kyc.isPending) return <VerificationSkeleton />;

  if (kyc.isError) {
    return (
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-fg">Верификация</h1>
        <InlineError
          className="mt-6"
          message={toMessage(kyc.error, 'Не удалось загрузить статус верификации.')}
          onRetry={() => void kyc.refetch()}
        />
      </div>
    );
  }

  const snapshot = kyc.data;
  const steps = buildSteps(snapshot);
  const verifiedCards = snapshot.cards.filter((card) => card.status === CardStatus.Verified);
  const isUnderReview = snapshot.status === KycStatus.Pending;
  const isApproved = snapshot.status === KycStatus.Approved;

  // A card is still a precondition of submitting, but it is now added on
  // «Реквизиты» — so the review block has to say where to go, not "see above".
  const hasCard = snapshot.cards.length > 0;

  const documentsDone = snapshot.passportUploaded && snapshot.selfieUploaded;
  const readyToSubmit = documentsDone && hasCard && !isUnderReview && !isApproved;
  const review = reviewCopy({ isApproved, isUnderReview, readyToSubmit, documentsDone });

  const handleSubmit = async (): Promise<void> => {
    try {
      await submitKyc.mutateAsync();
      toast.success('Отправлено на проверку', 'Администратор скоро посмотрит ваши документы.');
    } catch (error) {
      toast.error('Не удалось отправить', toMessage(error));
    }
  };

  return (
    <div>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-fg">Верификация</h1>
          <p className="mt-1.5 max-w-prose text-sm text-fg-secondary">
            Трафик идёт только на одобренные аккаунты с проверенной картой. Документы сдаются один
            раз; карты живут на отдельном экране «Реквизиты».
          </p>
        </div>
        <KycStatusBadge status={snapshot.status} />
      </header>

      <div className="mt-8 rounded-md border border-line bg-surface px-4 py-5 sm:px-6">
        <Stepper steps={steps} />
      </div>

      {snapshot.status === KycStatus.Rejected && snapshot.rejectionReason ? (
        <div className="mt-6 rounded-md border border-danger-500 bg-danger-50 px-4 py-3.5">
          <div className="flex items-start gap-3">
            <CircleAlert
              size={16}
              strokeWidth={1.5}
              aria-hidden="true"
              className="mt-0.5 shrink-0 text-danger-500"
            />
            <div>
              <p className="text-base font-semibold text-danger-700">Заявка отклонена</p>
              <p className="mt-1 max-w-prose text-sm leading-normal text-danger-700">
                {snapshot.rejectionReason}
              </p>
              {snapshot.reviewedAt ? (
                <p className="num mt-1.5 text-xs text-fg-muted">
                  Проверено {absoluteDateTime(snapshot.reviewedAt)}
                </p>
              ) : null}
              <p className="mt-2 max-w-prose text-sm text-fg-secondary">
                Устраните проблему выше, замените нужный документ ниже и отправьте заявку снова.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {isApproved && verifiedCards.length > 0 ? (
        <div className="mt-6 flex items-start gap-3 rounded-md border border-ok-500 bg-ok-50 px-4 py-3.5">
          <ShieldCheck
            size={16}
            strokeWidth={1.5}
            aria-hidden="true"
            className="mt-0.5 shrink-0 text-ok-700"
          />
          <div>
            <p className="text-base font-semibold text-ok-700">Аккаунт одобрен</p>
            <p className="mt-1 text-sm text-fg-secondary">
              Платежи направляются на{' '}
              {verifiedCards.length === 1
                ? 'вашу проверенную карту'
                : 'ваши проверенные карты'}
              .
            </p>
          </div>
        </div>
      ) : null}

      {/* Documents */}
      <SectionHeading>Документы</SectionHeading>
      <div className="grid gap-4 lg:grid-cols-2">
        <DocumentStep
          kind="passport"
          eyebrow="Шаг 1"
          title="Паспорт"
          requirements={PASSPORT_REQUIREMENTS}
          uploaded={snapshot.passportUploaded}
          locked={isUnderReview}
        />
        <DocumentStep
          kind="selfie"
          eyebrow="Шаг 2"
          title="Селфи с паспортом"
          requirements={SELFIE_REQUIREMENTS}
          uploaded={snapshot.selfieUploaded}
          locked={isUnderReview}
        />
      </div>

      {/* Review */}
      <SectionHeading>Проверка</SectionHeading>
      <Panel padding="loose">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-prose">
            <p className="text-base font-medium text-fg">{review.title}</p>
            <p className="mt-1.5 text-sm leading-normal text-fg-secondary">{review.body}</p>

            {!isApproved && !hasCard ? (
              <Link
                to="/cards"
                className={
                  'focus-ring tap-target mt-2.5 inline-flex items-center gap-1.5 rounded-sm ' +
                  'text-sm font-medium text-fg-accent underline underline-offset-2'
                }
              >
                Перейти к реквизитам
                <ArrowRight size={13} strokeWidth={1.5} aria-hidden="true" />
              </Link>
            ) : null}
          </div>

          {!isApproved ? (
            <Button
              variant="primary"
              size="lg"
              disabled={!readyToSubmit}
              isLoading={submitKyc.isPending}
              onClick={() => void handleSubmit()}
            >
              {submitKyc.isPending ? 'Отправляю…' : 'Отправить на проверку'}
            </Button>
          ) : null}
        </div>
      </Panel>
    </div>
  );
}

/**
 * What the review block says, in the order the trader hits it. Documents come
 * first because they are on this screen; the card is named last, together with
 * the screen it now lives on, so "finish the steps above" can't mislead.
 */
function reviewCopy({
  isApproved,
  isUnderReview,
  readyToSubmit,
  documentsDone,
}: {
  isApproved: boolean;
  isUnderReview: boolean;
  readyToSubmit: boolean;
  documentsDone: boolean;
}): { title: string; body: string } {
  if (isApproved) {
    return {
      title: 'Больше ничего не требуется',
      body:
        'Ваши документы и карты приняты. Новую карту можно добавить в любой момент ' +
        'на экране «Реквизиты» — каждая проверяется отдельно.',
    };
  }

  if (isUnderReview) {
    return {
      title: 'Ждём администратора',
      body: 'Проверка обычно занимает один рабочий день. Результат появится здесь и на обзоре.',
    };
  }

  if (readyToSubmit) {
    return {
      title: 'Можно отправлять',
      body:
        'Оба документа и хотя бы одна карта на месте. ' +
        'После отправки их нельзя менять до конца проверки.',
    };
  }

  if (!documentsDone) {
    return {
      title: 'Сначала загрузите документы',
      body:
        'Паспорт и селфи загружаются выше. Ещё понадобится хотя бы одна банковская карта — ' +
        'её добавляют на экране «Реквизиты».',
    };
  }

  return {
    title: 'Не хватает банковской карты',
    body:
      'Документы на месте. Добавьте хотя бы одну карту на экране «Реквизиты» — ' +
      'тогда заявку можно будет отправить.',
  };
}

function buildSteps(kyc: KycSnapshot): Step[] {
  const hasCard = kyc.cards.length > 0;
  const isSubmitted = kyc.status === KycStatus.Pending || kyc.status === KycStatus.Approved;

  const passport: Step = {
    id: 'passport',
    label: 'Паспорт',
    state: kyc.passportUploaded ? 'done' : 'current',
  };

  const selfie: Step = {
    id: 'selfie',
    label: 'Селфи',
    state: kyc.selfieUploaded ? 'done' : kyc.passportUploaded ? 'current' : 'todo',
  };

  const cards: Step = {
    id: 'cards',
    label: 'Банковские карты',
    state: hasCard ? 'done' : kyc.passportUploaded && kyc.selfieUploaded ? 'current' : 'todo',
  };

  const review: Step = {
    id: 'review',
    label: kyc.status === KycStatus.Approved ? 'Одобрено' : 'Проверка',
    state:
      kyc.status === KycStatus.Approved
        ? 'done'
        : isSubmitted || (kyc.passportUploaded && kyc.selfieUploaded && hasCard)
          ? 'current'
          : 'todo',
  };

  return [passport, selfie, cards, review];
}

function VerificationSkeleton() {
  return (
    <div role="status" aria-busy="true">
      <span className="sr-only">Загружаю статус верификации</span>
      <Skeleton className="h-7 w-44" />
      <Skeleton className="mt-3 h-3.5 w-80" />
      <div className="mt-8 rounded-md border border-line bg-surface px-6 py-6">
        <div className="flex flex-wrap gap-6">
          {Array.from({ length: 4 }, (_, index) => (
            <div key={index} className="min-w-[8rem] flex-1">
              <Skeleton className="h-6 w-6 rounded-full" />
              <Skeleton className="mt-2 h-2.5 w-12" />
              <Skeleton className="mt-1.5 h-3.5 w-24" />
            </div>
          ))}
        </div>
      </div>
      <div className="mt-12 grid gap-4 lg:grid-cols-2">
        {Array.from({ length: 2 }, (_, index) => (
          <div key={index} className="rounded-md border border-line bg-surface p-4">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="mt-4 h-3 w-full" />
            <Skeleton className="mt-2 h-3 w-5/6" />
            <Skeleton className="mt-5 h-20 w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}
